/**
 * Opens the global worker socket for the logged-in user — PURE CHAT TRANSPORT:
 * invite relay + room-status queries + relay health. It is NOT a status
 * source; all user-visible online/away/offline comes from Supabase presence
 * (see {@link useSupabaseFamilyPresence}, AC-14). Mount once (in MainLayout).
 *
 * @module features/p2p-chat/hooks/usePresence
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getPath, RouteKey } from '@/shared/constants/routes';
import { useNotificationStore } from '@/shared/stores/notificationStore';
import { openPresenceConnection } from '../lib/presence';
import { closeAll as closeAllChatSessions } from '../lib/sessionManager';
import { getSignalUrls } from '../lib/signalUrls';
import { useP2PChatStore } from '../stores/p2pChatStore';
import { usePresenceStore } from '../stores/presenceStore';

export function usePresence(): void {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const userName = useAuthStore((state) => state.user?.name ?? null);

  useEffect(() => {
    if (userId === null) {
      // Logged out (or not yet logged in): tear down any live chats so a new
      // user never inherits the previous session's connections/messages.
      closeAllChatSessions();
      useP2PChatStore.getState().clearAll();
      return;
    }

    const { ws } = getSignalUrls();
    const connection = openPresenceConnection({
      wsUrl: ws,
      userId,
      displayName: userName ?? userId,
      callbacks: {
        onConnected: (connected) => {
          usePresenceStore.getState().setConnected(connected);
        },
        // NOTE: no `onPresence` — the worker's presence broadcast is ignored;
        // Supabase Realtime Presence is the only status source (AC-14).
        onInvite: (invite) => {
          // Ignore invites we somehow sent to ourselves.
          if (invite.fromUserId === userId) return;
          // Dedup guard (AC-18): the relay may redeliver a queued invite
          // after a reconnect — never create a second Supabase row for the
          // same room code.
          const alreadyStored = useNotificationStore
            .getState()
            .notifications.some(
              (n) =>
                n.type === 'chat' && n.data?.['roomCode'] === invite.roomCode
            );
          if (alreadyStored) return;
          void useNotificationStore.getState().addNotification({
            type: 'chat',
            title: 'Покана за чат',
            message: `${invite.fromName} те покани на разговор`,
            data: {
              roomCode: invite.roomCode,
              fromUserId: invite.fromUserId,
              fromName: invite.fromName,
            },
            link: `${getPath(RouteKey.P2P_CHAT)}?invite=${invite.roomCode}`,
          });
        },
      },
    });

    return () => {
      connection.close();
      usePresenceStore.getState().clear();
    };
  }, [userId, userName]);
}
